/**
 * The shared submission contract of both write dialogs — bonus grant and bonus
 * reversal (design D12/D13).
 *
 * Deliberately small: one idempotency key for the dialog's lifetime, validation
 * once before the first submission, every field locked the moment a submission
 * is actually sent, and whatever the server said rendered as the server said it.
 * No cross-dialog session bookkeeping, no state outliving a closed dialog.
 *
 * Why the lock: the server's adjust idempotency scope is
 * `(sub_account_id, idempotency_key)` and its conflict fingerprint covers only
 * `(direction, amount, reason, is_bonus_grant)` — `expires_at` is excluded and
 * `BonusReversalRequest` fingerprints even less. A payload edited after an
 * unanswered submit is therefore *not* reliably rejected: switching the target
 * bucket lands outside the key's scope entirely (a genuine second credit), and
 * editing only the expiry returns the original record while the operator
 * believes the new expiry took effect. Locking at send time is what actually
 * makes "one key ⇒ one payload" true.
 */

export type DialogStatus =
  | 'editing' // nothing sent yet; fields editable
  | 'in-flight' // a submission is out; fields locked
  | 'rejected' // 4xx: server message, close only
  | 'unknown' // timeout / network / 5xx: retry unchanged, or close and check
  | 'indeterminate' // a retry answered non-2xx: go check the ledger, close only
  | 'closed'

export type DialogControl = 'submit' | 'cancel' | 'close' | 'retry-unchanged' | 'close-and-check'

export interface DialogState {
  /** One key per dialog session; discarded when the dialog closes. */
  idempotencyKey: string
  status: DialogStatus
  /** True once a submission has actually been sent — never cleared while open. */
  locked: boolean
  /** True once client-side validation has run; it runs exactly once. */
  validated: boolean
  message: string | null
  controls: readonly DialogControl[]
}

export type SubmissionOutcome =
  | { kind: 'success' }
  | { kind: 'rejected'; status: number; message: string }
  | { kind: 'unknown'; message?: string }

export type DialogEffect = 'success-toast' | 'refresh'

export interface OutcomeResult {
  state: DialogState
  effects: readonly DialogEffect[]
}

export const CHECK_LEDGER_MESSAGE =
  'Refresh the ledger to check whether this operation was already posted; do not start it again.'

export const UNKNOWN_OUTCOME_MESSAGE =
  'The result of this submission is unknown. It may or may not have been posted.'

/** Open a dialog: a fresh key, editable fields, nothing sent. */
export function openDialog(generateKey: () => string): DialogState {
  return {
    idempotencyKey: generateKey(),
    status: 'editing',
    locked: false,
    validated: false,
    message: null,
    controls: ['submit', 'cancel'],
  }
}

/**
 * A validation failure: nothing is sent, every field stays editable, and the key
 * is untouched (no submission has been made under it).
 */
export function failValidation(state: DialogState, message: string): DialogState {
  return { ...state, status: 'editing', locked: false, validated: false, message, controls: ['submit', 'cancel'] }
}

/**
 * Validation passed and the request is being sent: from here every field is
 * read-only until the dialog closes, so one key can only ever carry one payload.
 */
export function beginSubmit(state: DialogState): DialogState {
  return {
    ...state,
    status: 'in-flight',
    locked: true,
    validated: true,
    message: null,
    controls: [],
  }
}

/**
 * Re-send the identical payload under the identical key after an unknown
 * outcome. Deliberately re-runs **no** client-side validation: the locked bytes
 * go out as they are, including an `expires_at` that has since passed.
 */
export function beginRetry(state: DialogState): DialogState {
  return { ...state, status: 'in-flight', locked: true, message: null, controls: [] }
}

/**
 * Apply the server's answer.
 *
 * A non-2xx answer to a **retry** — `409`, a `400` for a now-past expiry, or
 * anything else — is terminal and instructs the operator to go check the ledger:
 * `409` proves only that the key carries a different payload, never that the
 * first write did not land, so the console never invites a new operation.
 */
export function applyOutcome(
  state: DialogState,
  outcome: SubmissionOutcome,
  options: { isRetry?: boolean } = {}
): OutcomeResult {
  const isRetry = options.isRetry ?? false

  if (outcome.kind === 'success') {
    return {
      state: { ...state, status: 'closed', message: null, controls: [] },
      effects: ['success-toast', 'refresh'],
    }
  }

  if (isRetry) {
    return {
      state: {
        ...state,
        status: 'indeterminate',
        locked: true,
        message: CHECK_LEDGER_MESSAGE,
        controls: ['close'],
      },
      effects: [],
    }
  }

  if (outcome.kind === 'rejected') {
    return {
      state: {
        ...state,
        status: 'rejected',
        locked: true,
        message: outcome.message,
        controls: ['close'],
      },
      effects: [],
    }
  }

  return {
    state: {
      ...state,
      status: 'unknown',
      locked: true,
      message: outcome.message ?? UNKNOWN_OUTCOME_MESSAGE,
      controls: ['retry-unchanged', 'close-and-check'],
    },
    effects: [],
  }
}

/** Closing discards the key; the next opening generates a new one. */
export function closeDialog(state: DialogState): DialogState {
  return { ...state, idempotencyKey: '', status: 'closed', message: null, controls: [] }
}

/** Whether the dialog owns the context (selector + account switching disabled). */
export function dialogOwnsContext(state: DialogState | null): boolean {
  return state != null && state.status !== 'closed'
}

/**
 * Classify an HTTP outcome. A `4xx` is a determinate rejection; a timeout, a
 * network error, or any `5xx` leaves the outcome unknown.
 */
export function classifyHttpOutcome(
  status: number | null,
  message: string
): SubmissionOutcome {
  if (status != null && status >= 400 && status < 500) {
    return { kind: 'rejected', status, message }
  }
  return { kind: 'unknown' }
}

/** Generate an idempotency key. Uses `crypto.randomUUID` where available. */
export function generateIdempotencyKey(): string {
  const globalCrypto = globalThis.crypto as Crypto | undefined
  if (globalCrypto?.randomUUID) return globalCrypto.randomUUID()
  return `acct-console-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}
